import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { notifications } from '../../lib/api';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import i18nInstance from '../../i18n';
import {
  AlertTriangle,
  Bell,
  CalendarDays,
  CheckCircle2,
  Dumbbell,
  Mail,
  Medal,
  MessageCircle,
  Send,
  ShieldAlert,
  Sparkles,
  Target,
  Trophy,
  Users,
  Utensils,
  Zap,
} from 'lucide-react';


const t = (key, fallback, options = {}) =>
  i18nInstance.t(key, typeof fallback === 'string' ? { defaultValue: fallback, ...options } : fallback);
const ToggleSwitch = ({ checked, onChange, disabled, testId }) => (
  <label className={`relative inline-flex min-h-11 shrink-0 items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
    <input
      type="checkbox"
      checked={Boolean(checked)}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="peer sr-only"
      data-testid={testId}
    />
    <span className="h-7 w-12 rounded-full bg-zinc-200 transition peer-checked:bg-lime-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lime-100" />
    <span className="absolute left-1 h-5 w-5 rounded-full bg-white shadow-sm transition peer-checked:translate-x-5" />
  </label>
);

const TYPE_META = {
  training: {
    Icon: Dumbbell,
    badge: 'bg-lime-100 text-lime-800 border-lime-200',
    dot: 'bg-lime-400',
  },
  schedule: {
    Icon: CalendarDays,
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    dot: 'bg-blue-500',
  },
  match: {
    Icon: Trophy,
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    dot: 'bg-orange-500',
  },
  coach: {
    Icon: MessageCircle,
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    dot: 'bg-purple-500',
  },
  achievement: {
    Icon: Medal,
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
  },
  progress: {
    Icon: Target,
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    dot: 'bg-emerald-500',
  },
  nutrition: {
    Icon: Utensils,
    badge: 'bg-teal-100 text-teal-800 border-teal-200',
    dot: 'bg-teal-500',
  },
  system: {
    Icon: ShieldAlert,
    badge: 'bg-red-100 text-red-800 border-red-200',
    dot: 'bg-red-500',
  },
};

const localeFor = (lang) => {
  if (String(lang || '').startsWith('ru')) return 'ru-RU';
  if (String(lang || '').startsWith('fr')) return 'fr-FR';
  return 'en-US';
};

const formatNotificationDate = (value, lang) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat(localeFor(lang), {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const getNotificationCategory = (type = '') => {
  const normalized = String(type).toLowerCase();
  if (normalized.includes('achievement')) return 'achievement';
  if (normalized.includes('training')) return 'training';
  if (normalized.includes('schedule')) return 'schedule';
  if (normalized.includes('match') || normalized.includes('tournament')) return 'match';
  if (normalized.includes('coach')) return 'coach';
  if (normalized.includes('progress')) return 'progress';
  if (normalized.includes('nutrition')) return 'nutrition';
  return 'system';
};

const getNotificationTypeKey = (type = '') => {
  const normalized = String(type).toLowerCase();
  if (normalized === 'achievement_unlocked') return 'notifications.newAchievement';
  if (normalized.includes('achievement')) return 'notifications.achievementUnlocked';
  return 'notifications.notificationDetails';
};

const getRelatedEntityTypeKey = (type = '') => {
  const normalized = String(type).toLowerCase();
  if (normalized === 'achievement') return 'notifications.achievementEntity';
  return '';
};

const normalizeInboxPayload = (data) => {
  if (Array.isArray(data)) {
    return { items: data, unreadCount: data.filter((item) => !item?.read_at).length };
  }
  const items = Array.isArray(data?.items) ? data.items : [];
  const unreadCount = Number.isFinite(data?.unread_count)
    ? data.unread_count
    : items.filter((item) => !item?.read_at).length;
  return { items, unreadCount };
};

const PreferenceRow = ({
  title,
  description,
  enabled,
  disabled,
  onChange,
  Icon,
  badgeClass,
  testId,
  children,
}) => (
  <div className={`rounded-2xl border bg-white p-5 shadow-sm transition ${enabled ? 'border-lime-200' : 'border-zinc-200'}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 gap-3">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${badgeClass}`}>
          <Icon size={19} />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold tracking-tight text-zinc-950">{title}</h3>
          <p className="mt-1 text-sm leading-6 text-zinc-500">{description}</p>
        </div>
      </div>
      <ToggleSwitch checked={enabled} onChange={onChange} disabled={disabled} testId={testId} />
    </div>
    {children && <div className="mt-5 border-t border-zinc-100 pt-5">{children}</div>}
  </div>
);

export const NotificationSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [inboxItems, setInboxItems] = useState([]);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [inboxError, setInboxError] = useState('');
  const [markingReadId, setMarkingReadId] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await notifications.getSettings();
      setSettings(response.data);
    } catch (error) {
      setLoadError(t('notifications.loadError'));
      toast.error(t('notifications.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const fetchNotifications = useCallback(async () => {
    setInboxLoading(true);
    setInboxError('');
    try {
      const response = await notifications.getAll({ limit: 50, offset: 0 });
      const { items, unreadCount } = normalizeInboxPayload(response.data);
      setInboxItems(items);
      setInboxUnreadCount(unreadCount);
    } catch (error) {
      setInboxError(t('notifications.loadInboxError'));
    } finally {
      setInboxLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleUpdateSettings = async (updates) => {
    setSaving(true);
    try {
      await notifications.updateSettings(updates);
      setSettings((current) => ({ ...current, ...updates }));
      toast.success(t('notifications.saveSuccess'));
    } catch (error) {
      toast.error(t('notifications.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!settings?.email_enabled) {
      toast.error(t('notifications.disabledMessage'));
      return;
    }

    setSendingTest(true);
    try {
      await notifications.sendTest();
      toast.success(t('notifications.testEmailSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('notifications.saveError'));
    } finally {
      setSendingTest(false);
    }
  };

  const handleMarkRead = async (notification) => {
    if (!notification?.id || notification.read_at || markingReadId) {
      return;
    }

    setMarkingReadId(notification.id);
    try {
      const response = await notifications.markRead(notification.id);
      const updated = response.data || {};
      setInboxItems((current) =>
        current.map((item) =>
          item.id === notification.id
            ? { ...item, ...updated, read_at: updated.read_at || item.read_at || new Date().toISOString() }
            : item
        )
      );
      setInboxUnreadCount((current) => Math.max(0, current - 1));
    } catch (error) {
      toast.error(t('notifications.markReadError'));
    } finally {
      setMarkingReadId('');
    }
  };

  const preferenceItems = useMemo(() => {
    return [
      {
        key: 'training_reminders',
        testId: 'toggle-training-reminders',
        title: t('notifications.trainingReminders'),
        description: t('notifications.trainingRemindersDesc'),
        Icon: Dumbbell,
        meta: TYPE_META.training,
      },
      {
        key: 'meal_reminders',
        testId: 'toggle-meal-reminders',
        title: t('notifications.nutritionReminders'),
        description: t('notifications.nutritionRemindersDesc'),
        Icon: Utensils,
        meta: TYPE_META.nutrition,
      },
      {
        key: 'opponent_notifications',
        testId: 'toggle-opponent-notifications',
        title: t('notifications.matchNotifications'),
        description: t('notifications.matchNotificationsDesc'),
        Icon: Users,
        meta: TYPE_META.coach,
      },
      {
        key: 'achievement_notifications',
        testId: 'toggle-achievement-notifications',
        title: t('notifications.achievementNotifications'),
        description: t('notifications.achievementNotificationsDesc'),
        Icon: Medal,
        meta: TYPE_META.achievement,
      },
      {
        key: 'court_booking_notifications',
        testId: 'toggle-court-booking-notifications',
        title: t('notifications.courtBookingNotifications'),
        description: t('notifications.courtBookingNotificationsDesc'),
        Icon: CalendarDays,
        meta: TYPE_META.schedule,
      },
      {
        key: 'feed_like_notifications',
        testId: 'toggle-feed-like-notifications',
        title: t('notifications.feedLikes'),
        description: t('notifications.feedLikesDesc'),
        Icon: Sparkles,
        meta: TYPE_META.progress,
      },
      {
        key: 'feed_comment_notifications',
        testId: 'toggle-feed-comment-notifications',
        title: t('notifications.feedComments'),
        description: t('notifications.feedCommentsDesc'),
        Icon: MessageCircle,
        meta: TYPE_META.coach,
      },
    ];
  }, [t]);

  const enabledPreferenceCount = preferenceItems.filter((item) => settings?.[item.key]).length;
  const totalPreferenceCount = preferenceItems.length;
  const emailEnabled = Boolean(settings?.email_enabled);
  const pushEnabled = Boolean(settings?.push_enabled);
  const unreadCount = Number.isFinite(inboxUnreadCount) ? inboxUnreadCount : 0;

  const summaryCards = [
    {
      label: t('notifications.unread'),
      value: `${unreadCount}`,
      detail: t('notifications.inboxSummary'),
      Icon: Bell,
      color: unreadCount > 0 ? 'text-lime-700' : 'text-zinc-700',
    },
    {
      label: t('notifications.activePreferences'),
      value: `${enabledPreferenceCount}/${totalPreferenceCount}`,
      detail: emailEnabled ? t('notifications.emailRulesEnabled') : t('notifications.emailChannelPaused'),
      Icon: CheckCircle2,
      color: emailEnabled ? 'text-lime-700' : 'text-zinc-600',
    },
    {
      label: t('notifications.emailChannel'),
      value: emailEnabled ? t('notifications.on') : t('notifications.off'),
      detail: t('notifications.managedByBackendSettings'),
      Icon: Mail,
      color: emailEnabled ? 'text-emerald-700' : 'text-rose-600',
    },
    {
      label: t('notifications.pushChannel'),
      value: pushEnabled ? t('notifications.on') : t('notifications.off'),
      detail: t('notifications.registrationManagedElsewhere'),
      Icon: Zap,
      color: pushEnabled ? 'text-blue-700' : 'text-zinc-600',
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="notification-settings-page">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="rounded-3xl bg-zinc-950 p-8">
            <div className="h-5 w-36 animate-pulse rounded bg-white/15" />
            <div className="mt-6 h-12 w-2/3 animate-pulse rounded bg-white/15" />
            <div className="mt-4 h-4 w-1/2 animate-pulse rounded bg-white/10" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="h-4 w-24 animate-pulse rounded bg-zinc-200" />
                <div className="mt-5 h-8 w-16 animate-pulse rounded bg-zinc-100" />
              </div>
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="h-5 w-40 animate-pulse rounded bg-zinc-200" />
                <div className="mt-3 h-4 w-2/3 animate-pulse rounded bg-zinc-100" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="notification-settings-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="relative overflow-hidden rounded-3xl bg-zinc-950 p-6 text-white shadow-2xl shadow-zinc-300/60 md:p-8">
          <div className="pointer-events-none absolute inset-0 opacity-25">
            <div className="absolute -right-20 top-6 h-60 w-60 rounded-full border border-lime-300" />
            <div className="absolute bottom-8 left-8 h-px w-72 rotate-6 bg-lime-300" />
            <div className="absolute bottom-20 left-1/2 h-px w-80 -rotate-12 bg-white/40" />
          </div>

          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-lime-200">
                <span className="h-2 w-2 rounded-full bg-lime-300" />
                {t('notifications.heroKicker')}
              </div>
              <h1 className="max-w-3xl text-4xl font-semibold tracking-tight md:text-5xl">{t('notifications.title')}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
                {t('notifications.heroBody')}
              </p>
            </div>
            <button
              onClick={handleSendTest}
              disabled={!settings?.email_enabled || sendingTest}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-lime-300 px-5 py-3 text-sm font-semibold text-zinc-950 shadow-lg shadow-lime-950/30 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-300"
              data-testid="send-test-button"
            >
              <Send size={18} />
              {sendingTest ? t('notifications.sending') : t('notifications.sendTest')}
            </button>
          </div>
        </section>

        {loadError && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-900">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 shrink-0 text-rose-600" size={22} />
                <div>
                  <h2 className="font-semibold">{t('notifications.loadErrorTitle')}</h2>
                  <p className="mt-1 text-sm text-rose-700">{loadError}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={fetchSettings}
                className="inline-flex min-h-11 items-center justify-center rounded-full bg-rose-600 px-5 text-sm font-semibold text-white"
              >
                {t('common.retry')}
              </button>
            </div>
          </div>
        )}

        {!loadError && (
          <>
            <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {summaryCards.map(({ label, value, detail, Icon, color }) => (
                <div key={label} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm font-medium text-zinc-500">{label}</span>
                    <Icon className={color} size={20} />
                  </div>
                  <div className={`text-3xl font-semibold tracking-tight ${color}`}>{value}</div>
                  <p className="mt-1 text-sm text-zinc-500">{detail}</p>
                </div>
              ))}
            </section>

            <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
              <div className="space-y-5">
                <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm" data-testid="email-settings">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex gap-3">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border ${
                        emailEnabled ? TYPE_META.progress.badge : 'border-zinc-200 bg-zinc-100 text-zinc-600'
                      }`}>
                        <Mail size={22} />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold tracking-tight text-zinc-950">{t('notifications.emailNotifications')}</h2>
                        <p className="mt-1 text-sm leading-6 text-zinc-500">{t('notifications.emailDescription')}</p>
                      </div>
                    </div>
                    <ToggleSwitch
                      checked={settings?.email_enabled}
                      onChange={(checked) => handleUpdateSettings({ email_enabled: checked })}
                      disabled={saving}
                      testId="toggle-email-enabled"
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {preferenceItems.map((item) => {
                    const enabled = Boolean(settings?.[item.key]);
                    const disabled = saving || !settings?.email_enabled;
                    return (
                      <PreferenceRow
                        key={item.key}
                        title={item.title}
                        description={item.description}
                        enabled={enabled}
                        disabled={disabled}
                        onChange={(checked) => handleUpdateSettings({ [item.key]: checked })}
                        Icon={item.Icon}
                        badgeClass={enabled && settings?.email_enabled ? item.meta.badge : 'border-zinc-200 bg-zinc-100 text-zinc-600'}
                        testId={item.testId}
                      >
                        {item.key === 'training_reminders' && enabled && settings?.email_enabled && (
                          <div data-testid="training-settings">
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-zinc-500">
                              {t('notifications.reminderTime')}
                            </label>
                            <select
                              className="min-h-11 w-full rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm font-semibold text-zinc-800 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                              value={settings?.training_reminder_time || 1}
                              onChange={(e) =>
                                handleUpdateSettings({ training_reminder_time: parseInt(e.target.value, 10) })
                              }
                              disabled={saving}
                              data-testid="select-reminder-time"
                            >
                              <option value="1">{t('notifications.reminderHour1')}</option>
                              <option value="24">{t('notifications.reminderHours24')}</option>
                            </select>
                          </div>
                        )}
                      </PreferenceRow>
                    );
                  })}
                </div>

                {!settings?.email_enabled && (
                  <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
                    <Bell size={44} className="mx-auto text-zinc-400" />
                    <h2 className="mt-4 text-xl font-semibold tracking-tight text-zinc-950">{t('notifications.emailPaused')}</h2>
                    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">{t('notifications.disabledMessage')}</p>
                  </div>
                )}
              </div>

              <aside className="space-y-5">
                <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm" data-testid="notification-inbox">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('notifications.inbox')}</h2>
                      <p className="text-sm text-zinc-500">{t('notifications.inboxBody')}</p>
                    </div>
                    <span className="rounded-full border border-zinc-200 bg-zinc-100 px-3 py-1 text-xs font-semibold text-zinc-600">
                      {t('notifications.unreadCount', { count: unreadCount })}
                    </span>
                  </div>

                  {inboxLoading && (
                    <div className="space-y-3">
                      {[0, 1, 2].map((item) => (
                        <div key={item} className="rounded-xl border border-zinc-100 bg-zinc-50 p-4">
                          <div className="h-4 w-28 animate-pulse rounded bg-zinc-200" />
                          <div className="mt-3 h-5 w-3/4 animate-pulse rounded bg-zinc-200" />
                          <div className="mt-2 h-4 w-full animate-pulse rounded bg-zinc-100" />
                        </div>
                      ))}
                    </div>
                  )}

                  {!inboxLoading && inboxError && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-900">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 shrink-0 text-rose-600" size={18} />
                        <div>
                          <h3 className="font-semibold">{t('notifications.loadInboxError')}</h3>
                          <p className="mt-1 text-sm text-rose-700">{inboxError}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={fetchNotifications}
                        className="mt-4 inline-flex min-h-10 items-center rounded-full bg-rose-600 px-4 text-sm font-semibold text-white"
                      >
                        {t('notifications.retry')}
                      </button>
                    </div>
                  )}

                  {!inboxLoading && !inboxError && inboxItems.length === 0 && (
                    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-5 text-center">
                      <Bell className="mx-auto text-zinc-400" size={34} />
                      <h3 className="mt-3 font-semibold text-zinc-950">{t('notifications.noNotificationsYet')}</h3>
                      <p className="mt-2 text-sm leading-6 text-zinc-500">
                        {t('notifications.inboxEmptyBody')}
                      </p>
                    </div>
                  )}

                  {!inboxLoading && !inboxError && inboxItems.length > 0 && (
                    <div className="space-y-3">
                      {inboxItems.map((notification) => {
                        const category = getNotificationCategory(notification.type);
                        const meta = TYPE_META[category] || TYPE_META.system;
                        const Icon = meta.Icon;
                        const isRead = Boolean(notification.read_at);
                        const typeLabel = t(getNotificationTypeKey(notification.type));
                        const createdAt = formatNotificationDate(notification.created_at, i18n.language);
                        const relatedTypeKey = getRelatedEntityTypeKey(notification.related_entity_type);
                        const relatedType = relatedTypeKey
                          ? t(relatedTypeKey)
                          : notification.related_entity_type;
                        const relatedText = notification.related_entity_type && notification.related_entity_id
                          ? t('notifications.relatedEntity', {
                              type: relatedType,
                              id: notification.related_entity_id,
                            })
                          : '';
                        const content = (
                          <>
                            <div className="flex items-start gap-3">
                              <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${meta.badge}`}>
                                <Icon size={18} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="mb-2 flex flex-wrap items-center gap-2">
                                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.badge}`}>
                                    {typeLabel}
                                  </span>
                                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                                    isRead ? 'bg-zinc-100 text-zinc-600' : 'bg-lime-100 text-lime-800'
                                  }`}>
                                    {isRead ? t('notifications.read') : t('notifications.unreadStatus')}
                                  </span>
                                </div>
                                <h3 className="text-sm font-semibold leading-5 text-zinc-950">
                                  {notification.title || t('notifications.notificationDetails')}
                                </h3>
                                {notification.message && (
                                  <p className="mt-1 text-sm leading-5 text-zinc-500">{notification.message}</p>
                                )}
                                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs font-medium text-zinc-500">
                                  {createdAt && <span>{createdAt}</span>}
                                  {relatedText && <span>{relatedText}</span>}
                                </div>
                              </div>
                            </div>
                            {!isRead && (
                              <span className="mt-4 inline-flex min-h-9 items-center rounded-full bg-zinc-950 px-4 text-xs font-semibold text-white">
                                {markingReadId === notification.id ? t('notifications.markingRead') : t('notifications.markAsRead')}
                              </span>
                            )}
                          </>
                        );

                        return isRead ? (
                          <div
                            key={notification.id}
                            className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-left"
                          >
                            {content}
                          </div>
                        ) : (
                          <button
                            key={notification.id}
                            type="button"
                            onClick={() => handleMarkRead(notification)}
                            disabled={Boolean(markingReadId)}
                            className="w-full rounded-xl border border-lime-200 bg-lime-50/60 p-4 text-left shadow-sm transition hover:border-lime-300 hover:bg-lime-50 disabled:cursor-wait disabled:opacity-75"
                          >
                            {content}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('notifications.channels')}</h2>
                      <p className="text-sm text-zinc-500">{t('notifications.channelsBody')}</p>
                    </div>
                    <Zap className="text-blue-700" size={21} />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3">
                      <span className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
                        <Mail size={16} />
                        {t('notifications.emailChannel')}
                      </span>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        emailEnabled ? 'bg-lime-100 text-lime-800' : 'bg-zinc-200 text-zinc-700'
                      }`}>
                        {emailEnabled ? t('notifications.active') : t('notifications.paused')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3">
                      <span className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
                        <Zap size={16} />
                        {t('notifications.pushChannel')}
                      </span>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        pushEnabled ? 'bg-blue-100 text-blue-800' : 'bg-zinc-200 text-zinc-700'
                      }`}>
                        {pushEnabled ? t('notifications.enabled') : t('notifications.notEnabled')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('notifications.typeCoverage')}</h2>
                      <p className="text-sm text-zinc-500">{t('notifications.typeCoverageBody')}</p>
                    </div>
                    <Target className="text-lime-700" size={21} />
                  </div>
                  <div className="space-y-3">
                    {preferenceItems.slice(0, 6).map((item) => (
                      <div key={item.key} className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3">
                        <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-zinc-700">
                          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${item.meta.dot}`} />
                          <span className="truncate">{item.title}</span>
                        </span>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                          settings?.email_enabled && settings?.[item.key] ? 'bg-lime-100 text-lime-800' : 'bg-zinc-200 text-zinc-700'
                        }`}>
                          {settings?.email_enabled && settings?.[item.key] ? t('notifications.on') : t('notifications.off')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </aside>
            </section>
          </>
        )}
      </div>
    </div>
  );
};
